import * as Sentry from "@sentry/react";

import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { RadioButtonTrueFalse } from "Components/Form/RadioButton";
import { RecentDamages } from "Components/Form/RecentDamages/index";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { Data } from "Components/TextContext/textInterfaces";
import { BackendProductInfo } from "Features/CheckPrice/Products/productConstants";
import { ProductProps } from "Features/CheckPrice/Products/productInterfaces";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { mapValidationErrorsDitto } from "Utils/formUtils";
import { getError } from "Utils/productUtils";
import React from "react";
import { TRAVEL_ABOUT_STEP } from "../travelSteps";
import { TravelProductIntroFormValues } from "./travelProductIntroInterfaces";
import { validationSchema } from "./travelProductIntroValidation";

const stepId = TRAVEL_ABOUT_STEP.id;

interface Props extends ProductProps {
  shortTitleTexts: Data[];
}

export const TravelProductIntro = ({
  updatePriceRequest,
  shortTitleTexts,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const initialFormState = useFormState(stepId, initialFormValues);
  const [
    {
      formText: { yourProduct },
    },
    lang,
  ] = useProductTexts(getFMSProductName(INSURANCE_NAME_VALUE.TRAVEL));

  const {
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  const handleSubmit = (formValues: TravelProductIntroFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        productName: INSURANCE_NAME_VALUE.TRAVEL,
        stepId,
        formState: {
          ...formValues,
        },
      }),
    );
    updatePriceRequest({
      productPriceUrl: BackendProductInfo.TRAVEL.priceReqUrl,
      stepForwardOnSuccess: true,
      updatedProductPriceRequestInfo: {
        injuries: formValues.injuries
          ? parseInt(formValues.injuries)
          : undefined,
        injuryYear: formValues.injuryYear
          ? parseInt(formValues.injuryYear)
          : undefined,
      },
    });
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductDetails - ${INSURANCE_NAME_VALUE.TRAVEL}`,
        );
      }}
    >
      <ProductIntroTile
        productBackendName={ProductBackendName.TRAVEL}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<TravelProductIntroFormValues>) => {
          return (
            <Form>
              <RadioButtonTrueFalse
                name="isInsuranceFor"
                id="isInsuranceFor"
                hint={yourProduct.isInsuranceFor.hint[lang]}
                label={yourProduct.isInsuranceFor.label[lang]}
                options={[
                  {
                    label: yourProduct.isInsuranceFor.options.myself[lang],
                    value: true,
                    id: "myself",
                  },
                  {
                    label:
                      yourProduct.isInsuranceFor.options.myselfAndFamily[lang],
                    value: false,
                    id: "myselfAndFamily",
                  },
                ]}
                direction="vertical"
                error={getError("isInsuranceFor", formProps)}
              />

              <RecentDamages
                formProps={formProps}
                productName={ProductBackendName.TRAVEL}
              />
              <PartnerProductIntroForms />
              <MarketingConsent />
              {priceCalculationResult.error && (
                <SystemErrorAlert alertId="priceCalculation" />
              )}
              <ErrorsSummedUp
                errors={mapValidationErrorsDitto(
                  formProps.errors,
                  shortTitleTexts,
                )}
                isVisible
              />
              <ScrollToFirstError />
              <NavigationButtons
                hidePreviousButton
                isLoading={priceCalculationResult.isLoading}
              />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.TRAVEL}
                stepId={stepId}
              />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};

const initialFormValues = {
  isInsuranceFor: undefined,
  injuries: undefined,
  injuryYear: undefined,
};
